/*
 * @providesModule APIUtilities
 *
 */

import _ from 'lodash';
import {v4 as uuidv4} from 'uuid';
import * as base64 from 'base64-js';

import {NativeModules, Platform} from 'react-native';

const SkillzModule = NativeModules.SkillzModule;
const SKZAPIManager = NativeModules.SKZAPIManager;
const RandomBytes = NativeModules.RNRandomBytes;

const v2Endpoints = ['store/disable_stored_card', 'matchmaker/matches'];

// These control allowed versions for API calls to specify
const versionPrefixes = ['/v1/', '/v2/'];

export default class APIUtilities {
  static getRandomBytes(numBytes: number): Promise<number[]> {
    return new Promise((resolve: (bytes: number[]) => void) => {
      RandomBytes.randomBytes(numBytes, (error: unknown, bytesString: string) => {
        let processedBytes = bytesString;
        const BYTE_SIZE = 4;
        const remainder = processedBytes.length % BYTE_SIZE;
        if (remainder > 0) {
          processedBytes = processedBytes.padEnd(
            processedBytes.length + (BYTE_SIZE - remainder),
            '='
          );
        }

        resolve(base64.toByteArray(processedBytes));
      });
    });
  }

  static async createUUID(): Promise<string> {
    const numBytesForv4 = 16;
    const randomBytes = await APIUtilities.getRandomBytes(numBytesForv4);
    return uuidv4({random: randomBytes});
  }

  static checkValidURL(url: string): boolean {
    return !_.includes(url, 'undefined') && !_.includes(url, 'null') && url;
  }

  static generateURLForSubpath(url: string): string {
    /* NOTE: If a url contains a version specifier (v1, v2, etc), we should leave it be
     * This gives us more flexibility and can rely on older endpoints for legacy features
     * where they are known to be stable while also enabling us to use new versions.
     * If a call does not specify which /vX/ they want, we will append one of them here
     */
    let hasVersionPrefix = false;
    _.map(versionPrefixes, (prefix: string) => {
      hasVersionPrefix = hasVersionPrefix || url.toString().includes(prefix);
    });
    if (hasVersionPrefix) {
      return url;
    }

    return this._determineEndpointVersionForPath(url) + url;
  }

  static _determineEndpointVersionForPath(url: string): string {
    if (url == 'geolocation-license') {
      return '/';
    }
    return _.includes(v2Endpoints, url) ? '/v2/' : '/v1/';
  }

  static _shouldUsePaymentURL(isTransaction: boolean): boolean {
    return (
      SKZAPIManager.CurrentEnvironment == SKZAPIManager.Environment.Prod &&
      isTransaction &&
      Platform.OS !== 'web'
    );
  }

  static _getServerURL(): string {
    if (Platform.OS == 'ios') {
      const url = SKZAPIManager.SERVER_URL;
      return url.substring(0, url.indexOf('/v1'));
    } else {
      return SkillzModule.SERVER_URL;
    }
  }

  static generateURL(url: string, isTransaction: boolean = false): string {
    const serverURL = this._getServerURL();
    const baseURL = this._shouldUsePaymentURL(isTransaction)
      ? 'https://payment.skillz.com'
      : serverURL;
    const subURL = this.generateURLForSubpath(url);
    const fullURL = baseURL + subURL;

    if (global.__DEV__) {
      //console.debug('full URL: ' + fullURL);
    }

    return fullURL;
  }
}
